import type { Decor } from '../world/mapdef';
import { block, ellipse, hash, rect, shade, shadow, sun, type Ctx } from './paint';
import { wind } from './nature';
import { drawCharacter, type CharLook, type Facing } from './sprites';

/**
 * Street-life props shared by the Taipei district packages (xinyi `xy_*`, cks `ck_*`): crowds with
 * shopping bags, umbrellas and suitcases, people sitting on benches, queues, cats, utility poles with
 * cables, vertical shop signs, food trucks, flower parterres, ground paint (manholes, lane arrows,
 * puddles, fallen leaves)... Each package dispatches the kind suffix here (`drawLife(c, 'crowd', d, t)`),
 * so the same drawer serves `xy_crowd` and `ck_crowd`.
 *
 * Everything is drawn straight from the map `Decor` (bottom-centre anchor). Characters use the game's
 * own character renderer, so crowds match the player's scale (~24 px tall).
 */

const CJK = '"PingFang TC", "Noto Sans TC", "Noto Sans CJK TC", "Microsoft JhengHei", sans-serif';
const isNight = () => sun.alpha < 0.8;

function txt(c: Ctx, s: string, x: number, y: number, size: number, col: string, weight = 900, shadowCol?: string): void {
  c.font = `${weight} ${size}px ${CJK}`;
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  if (shadowCol) {
    c.fillStyle = shadowCol;
    c.fillText(s, x + 0.4, y + 0.5);
  }
  c.fillStyle = col;
  c.fillText(s, x, y);
}

function line(c: Ctx, col: string, w: number, pts: number[]): void {
  c.strokeStyle = col;
  c.lineWidth = w;
  c.beginPath();
  c.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) c.lineTo(pts[i], pts[i + 1]);
  c.stroke();
}

function poly(c: Ctx, col: string, pts: number[]): void {
  c.fillStyle = col;
  c.beginPath();
  c.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) c.lineTo(pts[i], pts[i + 1]);
  c.closePath();
  c.fill();
}

/** Offscreen cache for static pictures (same pattern as `cachedArt` in propsCks). */
const bitmaps = new Map<string, { cv: HTMLCanvasElement; s: number }>();
function cached(c: Ctx, key: string, x0: number, y0: number, w: number, h: number, draw: (cc: Ctx) => void): void {
  const m = c.getTransform();
  const s = Math.min(3, Math.max(1, Math.round(Math.hypot(m.a, m.b) * 2) / 2));
  let e = bitmaps.get(key);
  if (!e || e.s !== s) {
    const cv = document.createElement('canvas');
    cv.width = Math.ceil(w * s);
    cv.height = Math.ceil(h * s);
    const cc = cv.getContext('2d')!;
    cc.scale(s, s);
    cc.translate(-x0, -y0);
    draw(cc);
    e = { cv, s };
    bitmaps.set(key, e);
  }
  c.drawImage(e.cv, x0, y0, w, h);
}

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

const SKINS = ['#f6cfa8', '#f3c9a0', '#e8b48c', '#d9a47a', '#c88a5a', '#f8d8bc', '#eac19c', '#f0c8a4'];
const DARK_HAIR = ['#1d1a22', '#2a1d16', '#3a2418', '#141218', '#4a2a1a'];
const GREY_HAIR = ['#9a948a', '#c9c4bc', '#e8e4dc', '#8f8a86'];
const STYLES = ['short', 'long', 'pony', 'bun', 'cap', 'pigtails', 'messy', 'neat', 'curly'] as const;
const TOPS: [string, string][] = [
  ['#f4f1ea', '#cfc8bb'], ['#3a8fd9', '#2a6fb0'], ['#e2493b', '#b0302a'], ['#ffd34e', '#d8b030'], ['#1b1d24', '#0e1014'],
  ['#57c278', '#3a9a58'], ['#9b5fc0', '#7a3fa0'], ['#ff6aa0', '#d04a80'], ['#ff9f43', '#d07a2a'], ['#7ad0e0', '#4aa0b0'],
  ['#5a6f8a', '#44566e'], ['#2f8a7a', '#1f6a5a'], ['#c8b48a', '#a89468'], ['#8a3a2a', '#6a2a1e'], ['#f8e0b0', '#d8c090'],
];
const BOTTOMS = ['#2f3442', '#3a4a7a', '#1d1a22', '#7a5a3a', '#4a6a8a', '#c8c0b0', '#5a5a4a', '#2a2f3d'];
const SHOES = ['#f4f1ea', '#1d1814', '#e2493b', '#3b2416', '#c8cdd2'];

/** 32 looks: office workers, students, tourists, elders, kids. Index with `look % LOOKS.length`. */
export const LOOKS: CharLook[] = Array.from({ length: 32 }, (_, i) => {
  const kind = i % 8; // 0-1 office, 2 student, 3 elder, 4 kid, 5-7 casual
  const h = (k: number) => hash(i, k, 77);
  const elder = kind === 3, kid = kind === 4, office = kind <= 1, student = kind === 2;
  const [top, topShade] = office ? (i % 4 ? ['#f4f6f8', '#cfd6dc'] : ['#2a2f3d', '#1b1d24']) : student ? ['#f4f1ea', '#cfc8bb'] : TOPS[Math.floor(h(1) * TOPS.length)];
  return {
    skin: SKINS[Math.floor(h(2) * SKINS.length)],
    hair: elder ? GREY_HAIR[Math.floor(h(3) * GREY_HAIR.length)] : h(3) < 0.12 ? '#a8432a' : DARK_HAIR[Math.floor(h(4) * DARK_HAIR.length)],
    hairStyle: kid ? (['cap', 'pigtails', 'short', 'pony'] as const)[Math.floor(h(5) * 4)] : STYLES[Math.floor(h(5) * STYLES.length)],
    hat: ['#e2493b', '#1b1d24', '#3a8fd9', '#ffffff', '#ffd34e', '#2f8a7a'][Math.floor(h(6) * 6)],
    top,
    topShade,
    bottom: office ? '#2a2f3d' : student ? '#2f4a8a' : BOTTOMS[Math.floor(h(7) * BOTTOMS.length)],
    shoes: SHOES[Math.floor(h(8) * SHOES.length)],
    dress: !office && !kid && h(9) < 0.3,
    glasses: h(10) < 0.2 || (office && h(10) < 0.4),
    small: kid,
    tie: office && i % 4 === 1 ? '#2f5fa8' : undefined,
    badge: student ? '#e2493b' : office && h(11) < 0.5 ? '#3a8fd9' : undefined,
  };
});
const FACINGS: Facing[] = ['down', 'up', 'left', 'right'];

/** Accessories a person can carry (`acc` in `person`): 1 bags, 2 parasol, 3 suitcase, 4 phone up, 5 bubble tea, 6 hiker, 7 balloon, 8 camera. */
export type Acc = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

const BAG_COLS = ['#e2493b', '#f4f1ea', '#2a2f3d', '#ffd34e', '#3a8fd9', '#ff9fc0', '#8a5a3a'];

/** One standing person with an accessory. `seed` varies blink and small details. */
export function person(c: Ctx, x: number, y: number, look: number, facing: Facing, acc: Acc, t: number, seed = 0): void {
  const lk = LOOKS[((look % LOOKS.length) + LOOKS.length) % LOOKS.length];
  const s = hash(Math.round(x), Math.round(y), 91) + seed;
  const breath = (t * 0.45 + s) % 1;
  const sideL = facing === 'left', sideR = facing === 'right';
  // Things that hang behind the body are drawn first.
  if (acc === 6 && facing !== 'down') {
    // Hiker's backpack.
    block(c, x - 3.4, y - 19, 6.8, 9, '#e2493b', '#ff6a5a', '#a02a1e');
    rect(c, '#1b1d24', x - 3, y - 16, 6, 0.8);
  }
  if (acc === 3 && facing !== 'down') suitcase(c, x + (sideL ? -8 : 7), y, s);
  drawCharacter(c, lk, x, y, { facing, phase: 0, moving: false, blink: (t + s * 7) % 4 < 0.12, breath });
  switch (acc) {
    case 1: {
      // Shopping bags in both hands (one on the side when seen from the side).
      const cols = [BAG_COLS[Math.floor(s * 7) % 7], BAG_COLS[Math.floor(s * 13) % 7]];
      const sides = sideL ? [-1] : sideR ? [1] : [-1, 1];
      sides.forEach((sd, i) => {
        const bx = x + sd * 6.2, by = y - 9;
        line(c, '#5a4a3a', 0.5, [bx - 1.2, by - 1, bx, by - 4, bx + 1.2, by - 1]);
        block(c, bx - 2.4, by - 1, 4.8, 6.4, cols[i], shade(cols[i], 0.25), shade(cols[i], -0.3));
      });
      break;
    }
    case 2: {
      // A parasol (very Taiwanese sun protection) or an umbrella on grey days.
      const col = ['#f4f1ea', '#ffd34e', '#ff9fc0', '#2a2f3d', '#7ad0e0'][Math.floor(s * 5) % 5];
      const ux = x + (sideL ? -3 : sideR ? 3 : 0), uy = y - 31;
      rect(c, '#55606a', ux - 0.4, uy, 0.8, 16);
      c.fillStyle = col;
      c.beginPath();
      c.ellipse(ux, uy, 11, 4.5, 0, Math.PI, 0);
      c.fill();
      c.fillStyle = shade(col, -0.25);
      c.beginPath();
      c.ellipse(ux + 4, uy, 7, 4.5, 0, Math.PI, Math.PI * 1.5);
      c.fill();
      ellipse(c, '#55606a', ux, uy - 4.5, 0.8, 0.8);
      ellipse(c, 'rgba(0,0,0,0.12)', x, y + 0.4, 10, 2.6);
      break;
    }
    case 3:
      if (facing === 'down') suitcase(c, x + 7, y, s);
      break;
    case 4: {
      // Phone held up for a photo.
      const px = x + (sideL ? -5 : sideR ? 5 : 3.5), py = y - 24;
      block(c, px - 1.4, py, 2.8, 4.6, '#1b1d24', '#4a4e54');
      rect(c, isNight() ? '#bfe6ff' : '#9ad0f0', px - 0.9, py + 0.6, 1.8, 3.2);
      if (Math.sin(t * 0.9 + s * 9) > 0.985) ellipse(c, 'rgba(255,255,255,0.8)', px, py + 2, 5, 5);
      break;
    }
    case 5: {
      // A cup of bubble tea with a fat straw.
      const bx = x + (sideL ? -4.5 : sideR ? 4.5 : 5), by = y - 14;
      block(c, bx - 1.6, by, 3.2, 4.6, '#e8d8c0', '#fff6e8', '#b8a080');
      for (let k = 0; k < 3; k++) ellipse(c, '#2a1810', bx - 0.8 + k * 0.8, by + 3.6, 0.45, 0.45);
      rect(c, '#e2493b', bx - 0.3, by - 4, 0.8, 5);
      break;
    }
    case 6: {
      // Hiking pole and a sun hat.
      const hx = x + (sideL ? -5 : 5);
      line(c, '#8a9096', 0.8, [hx, y - 14, hx + 0.6, y]);
      ellipse(c, '#1b1d24', hx + 0.6, y, 1, 0.5);
      ellipse(c, '#c8a860', x, y - 23.5, 5.5, 1.6);
      break;
    }
    case 7: {
      const col = ['#e2493b', '#ffd34e', '#3a8fd9', '#ff8ac0', '#57c278'][Math.floor(s * 5) % 5];
      const hx = x + (sideL ? -4 : 4), hy = y - 12;
      const bx = hx + Math.sin(t * 1.1 + s * 6) * 1.6 + wind.at(hx, hy) * 2, by = hy - 20 + Math.sin(t * 0.7 + s) * 1;
      line(c, 'rgba(255,255,255,0.7)', 0.35, [hx, hy, bx, by + 4]);
      ellipse(c, col, bx, by, 3.4, 4.2);
      ellipse(c, 'rgba(255,255,255,0.5)', bx - 1.1, by - 1.5, 0.9, 1.2);
      break;
    }
    case 8: {
      // A camera with a big lens held at the chest.
      const px = x + (sideL ? -4 : sideR ? 4 : 0), py = y - 13;
      block(c, px - 3, py - 1.5, 6, 4, '#1b1d24', '#4a4e54');
      ellipse(c, '#2a2e33', px + 0.4, py + 0.6, 1.8, 1.8);
      ellipse(c, '#6aa0c8', px + 0.4, py + 0.6, 0.9, 0.9);
      break;
    }
    default:
      if (acc === 0 && facing === 'down' && s % 1 > 0.7) {
        // Someone checking their phone.
        block(c, x + 1.4, y - 12, 2.6, 3.6, '#1b1d24');
        rect(c, isNight() ? '#bfe6ff' : '#7ab0d0', x + 1.9, y - 11.4, 1.6, 2.4);
      }
  }
}

function suitcase(c: Ctx, x: number, y: number, s: number): void {
  const col = ['#2a2f3d', '#e2493b', '#c8cdd2', '#3a8fd9', '#ffd34e'][Math.floor(s * 5) % 5];
  ellipse(c, 'rgba(20,10,6,0.25)', x, y, 4, 1.2);
  ellipse(c, '#1b1d24', x - 2, y - 0.6, 1, 1);
  ellipse(c, '#1b1d24', x + 2, y - 0.6, 1, 1);
  block(c, x - 3.4, y - 12, 6.8, 11, col, shade(col, 0.25), shade(col, -0.3));
  rect(c, shade(col, -0.2), x - 3, y - 7, 6, 0.6);
  rect(c, '#8a9096', x - 1.2, y - 17, 0.7, 5);
  rect(c, '#8a9096', x + 0.5, y - 17, 0.7, 5);
  rect(c, '#8a9096', x - 1.4, y - 17.5, 2.8, 0.9);
}

/**
 * `variant` of a `person` decor: acc * 128 + look * 4 + facing. Build it with `pv(look, facing, acc)`.
 */
export const pv = (look: number, facing: 0 | 1 | 2 | 3, acc: Acc = 0): number => acc * 128 + (look % 32) * 4 + facing;

function personDecor(c: Ctx, d: Decor, t: number): void {
  const v = d.variant ?? 0;
  const acc = Math.floor(v / 128) as Acc;
  const base = v % 128;
  person(c, d.x, d.y, Math.floor(base / 4), FACINGS[base % 4], acc, t);
}

/**
 * A crowd spread over `w` × `h` around the anchor. `variant` = count + 16 * seed + 256 * mode:
 * mode 0 mixed, 1 shoppers (bags), 2 tourists (photos, parasols), 3 travellers (suitcases),
 * 4 hikers, 5 all facing up (watching something north), 6 all facing down (facing the viewer), 7 all facing left, 8 all facing right.
 */
function crowd(c: Ctx, d: Decor, t: number): void {
  const v = d.variant ?? 4;
  const n = Math.max(1, v % 16), seed = Math.floor(v / 16) % 16, mode = Math.floor(v / 256);
  const w = d.w ?? 48, h = d.h ?? 20;
  const people: [number, number, number, Facing, Acc][] = [];
  for (let i = 0; i < n; i++) {
    const x = d.x + (hash(i, seed, 301) - 0.5) * w, y = d.y + (hash(i, seed, 302) - 0.5) * h;
    const look = Math.floor(hash(i, seed, 303) * 32);
    const r = hash(i, seed, 304);
    let facing = FACINGS[Math.floor(hash(i, seed, 305) * 4)];
    let acc: Acc = 0;
    if (mode === 1) acc = r < 0.6 ? 1 : r < 0.75 ? 5 : r < 0.85 ? 2 : 0;
    else if (mode === 2) acc = r < 0.3 ? 4 : r < 0.5 ? 2 : r < 0.6 ? 8 : r < 0.7 ? 1 : 0;
    else if (mode === 3) acc = r < 0.55 ? 3 : r < 0.7 ? 1 : r < 0.8 ? 5 : 0;
    else if (mode === 4) acc = r < 0.7 ? 6 : 0;
    else acc = r < 0.15 ? 1 : r < 0.25 ? 5 : r < 0.32 ? 2 : r < 0.38 ? 4 : r < 0.42 ? 7 : 0;
    if (mode === 5) facing = 'up';
    else if (mode === 6) facing = 'down';
    else if (mode === 7) facing = 'left';
    else if (mode === 8) facing = 'right';
    people.push([x, y, look, facing, acc]);
  }
  people.sort((a, b) => a[1] - b[1]);
  for (const [x, y, look, facing, acc] of people) person(c, x, y, look, facing, acc, t);
}

/**
 * People queueing. The anchor is the head of the queue (next to the counter / door); `variant` =
 * count + 16 * seed + 256 * dir (0 facing right, queue extends west; 1 facing left, extends east; 2 facing up, extends south).
 */
function queue(c: Ctx, d: Decor, t: number): void {
  const v = d.variant ?? 4;
  const n = Math.max(1, v % 16), seed = Math.floor(v / 16) % 16, dir = Math.floor(v / 256);
  const step = 11;
  for (let j = n - 1; j >= 0; j--) {
    const x = dir === 2 ? d.x + (hash(j, seed, 311) - 0.5) * 3 : d.x + (dir ? 1 : -1) * j * step;
    const y = dir === 2 ? d.y + j * 9 : d.y + (hash(j, seed, 312) - 0.5) * 3;
    const look = Math.floor(hash(j, seed, 313) * 32);
    const r = hash(j, seed, 314);
    const acc: Acc = r < 0.25 ? 3 : r < 0.4 ? 1 : r < 0.5 ? 5 : 0;
    person(c, x, y, look, dir === 2 ? 'up' : dir ? 'left' : 'right', acc, t);
  }
}

/** A park bench with one or two people sitting on it. `variant` = seed; `w` = bench width (default 30). */
function benchSit(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, w = d.w ?? 30, v = d.variant ?? 0;
  const n = 1 + (hash(v, 1, 321) < 0.6 ? 1 : 0);
  shadow(c, x, y, w / 2 + 2, 2.6);
  // Back rest first, then the sitters, then the seat (it hides their legs).
  block(c, x - w / 2, y - 17, w, 3.2, '#9a6a40', '#b8885a', '#6a4a2a');
  block(c, x - w / 2, y - 13.2, w, 2.4, '#9a6a40', '#b8885a', '#6a4a2a');
  for (let i = 0; i < n; i++) {
    const px = n === 1 ? x + (hash(v, 2, 322) - 0.5) * 8 : x + (i ? 1 : -1) * (w / 4);
    const look = Math.floor(hash(v, 3 + i, 323) * 32);
    const r = hash(v, 5 + i, 324);
    person(c, px, y - 7, look, 'down', r < 0.3 ? 5 : r < 0.45 ? 1 : 0, t, i);
  }
  block(c, x - w / 2, y - 10, w, 4.2, '#a8784a', '#c8986a', '#6a4a2a');
  rect(c, 'rgba(0,0,0,0.15)', x - w / 2 + 1, y - 8.2, w - 2, 0.6);
  for (const lx of [x - w / 2 + 3, x + w / 2 - 4.6]) {
    rect(c, '#3a3e44', lx, y - 6, 1.6, 6);
    rect(c, '#3a3e44', lx, y - 18, 1.6, 2);
  }
}

/** A photographer behind a camera on a tripod, facing up (towards the view). */
function tripod(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, v = d.variant ?? 0;
  person(c, x, y, 8 + v * 3, 'up', 0, t);
  const tx = x + 9, ty = y - 2;
  line(c, '#2a2e33', 0.9, [tx, ty - 22, tx - 4, ty]);
  line(c, '#2a2e33', 0.9, [tx, ty - 22, tx + 4, ty]);
  line(c, '#2a2e33', 0.9, [tx, ty - 22, tx + 0.5, ty + 1]);
  block(c, tx - 3.6, ty - 27, 7.2, 5, '#1b1d24', '#4a4e54');
  block(c, tx - 1.6, ty - 25.6, 3.2, 2.6, '#2a2e33', '#5a5e64');
  if (Math.sin(t * 0.7 + v) > 0.99) ellipse(c, 'rgba(255,255,255,0.85)', tx, ty - 28, 6, 4);
}

// ---------------------------------------------------------------------------
// Animals
// ---------------------------------------------------------------------------

const CAT_COLS: [string, string][] = [['#e08a3a', '#f8b060'], ['#2a2a30', '#4a4a52'], ['#c8c0b0', '#ece6da'], ['#8a8a92', '#b0b0b8'], ['#f4f1ea', '#e08a3a']];

/** A cat: `variant` = colour + 8 * pose (0 sitting, 1 lying), flipped by `w` < 0; `h` lifts it onto a roof or wall. */
function cat(c: Ctx, d: Decor, t: number): void {
  const v = d.variant ?? 0, [col, light] = CAT_COLS[v % 5], pose = Math.floor(v / 8) % 3;
  const x = d.x, y = d.y - (d.h ?? 0), dir = (d.w ?? 1) < 0 ? -1 : 1;
  const s = hash(Math.round(x), Math.round(y), 331);
  ellipse(c, 'rgba(20,10,6,0.22)', x, y, pose === 1 ? 6 : 4.2, 1.3);
  const tail = Math.sin(t * 1.6 + s * 9) * 2;
  if (pose === 1) {
    ellipse(c, col, x, y - 2.4, 6, 2.6);
    ellipse(c, light, x - dir * 1, y - 2.6, 3.2, 1.4);
    line(c, col, 1.3, [x - dir * 5, y - 2, x - dir * 8, y - 3 + tail, x - dir * 10, y - 1.5 + tail]);
    ellipse(c, col, x + dir * 5, y - 4, 2.6, 2.3);
    poly(c, col, [x + dir * 3.2, y - 5.6, x + dir * 2.6, y - 8.2, x + dir * 4.6, y - 6.2]);
    poly(c, col, [x + dir * 6.8, y - 5.6, x + dir * 7.4, y - 8.2, x + dir * 5.4, y - 6.2]);
  } else {
    ellipse(c, col, x, y - 3, 3.2, 3.4);
    ellipse(c, light, x, y - 2.4, 1.8, 2);
    line(c, col, 1.3, [x - dir * 2.6, y - 1.2, x - dir * 6, y - 2 + tail, x - dir * 7.5, y - 5 + tail]);
    ellipse(c, col, x + dir * 0.4, y - 7.6, 2.7, 2.4);
    poly(c, col, [x - 1.8, y - 9.2, x - 2.4, y - 11.8, x - 0.2, y - 10]);
    poly(c, col, [x + 2.6, y - 9.2, x + 3.2, y - 11.8, x + 1, y - 10]);
    const blink = (t + s * 5) % 5 < 0.15;
    if (!blink) {
      ellipse(c, '#5ad070', x - 0.7 + dir * 0.4, y - 7.9, 0.55, 0.7);
      ellipse(c, '#5ad070', x + 1.5 + dir * 0.4, y - 7.9, 0.55, 0.7);
    }
    rect(c, '#e8a0a0', x + 0.1 + dir * 0.4, y - 6.9, 0.7, 0.5);
  }
}

/** A few pigeons pecking (variant = seed), lifting off now and then. */
function pigeons(c: Ctx, d: Decor, t: number): void {
  const v = d.variant ?? 0, w = d.w ?? 30;
  const cyc = (t + v * 5) % 30;
  const flying = cyc > 24;
  const u = flying ? (cyc - 24) / 6 : 0;
  for (let i = 0; i < 5; i++) {
    const px = d.x + (hash(i, v, 341) - 0.5) * w + Math.sin(t * 0.3 + i) * 2;
    const py = d.y + (hash(i, v, 342) - 0.5) * 10;
    const dir = hash(i, v, 343) < 0.5 ? -1 : 1;
    if (flying) {
      const alt = Math.sin(u * Math.PI) * 40, f = Math.sin(t * 18 + i) * 2.2;
      const fx = px + dir * u * 26, fy = py - alt;
      poly(c, '#7a8088', [fx - 1, fy, fx - 5, fy - f, fx - 2, fy + 0.6]);
      poly(c, '#7a8088', [fx + 1, fy, fx + 5, fy - f, fx + 2, fy + 0.6]);
      ellipse(c, '#9aa0a8', fx, fy, 2, 1.1);
      continue;
    }
    const peck = Math.max(0, Math.sin(t * 3 + i * 1.7)) > 0.7 ? 1.2 : 0;
    ellipse(c, 'rgba(20,10,6,0.2)', px, py, 2.6, 0.8);
    ellipse(c, '#8a8e96', px, py - 2, 2.6, 1.7);
    ellipse(c, '#6a6e78', px - dir * 1.2, py - 2.3, 1.4, 1.1);
    ellipse(c, '#5a6070', px + dir * 2.2, py - 3.4 + peck, 1.1, 1);
    rect(c, '#4aa07a', px + dir * 1.4, py - 3, 0.9, 0.6);
    rect(c, '#e8a060', px + dir * 3.2, py - 3.3 + peck, 0.7, 0.4);
  }
}

// ---------------------------------------------------------------------------
// Street furniture and signs
// ---------------------------------------------------------------------------

/** Concrete utility pole with a transformer and a bundle of cables sagging `w` px to the right (variant 1: cables only). */
function cables(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, w = d.w ?? 128, top = y - 62;
  const only = d.variant === 1;
  if (!only) {
    shadow(c, x, y, 3, 1.2, 0.3, 40);
    block(c, x - 1.6, top, 3.2, y - top, '#9a9690', '#b8b4ac', '#6a6660');
    for (let k = 0; k < 4; k++) rect(c, 'rgba(0,0,0,0.12)', x - 1.6, top + 8 + k * 12, 3.2, 0.8);
    // Cross-arm with insulators and a grey transformer can.
    rect(c, '#55606a', x - 9, top + 4, 18, 1.4);
    for (const ix of [-7, -2.5, 2.5, 7]) block(c, x + ix - 0.8, top + 1.6, 1.6, 2.6, '#c8cdd2', '#e8ecef');
    block(c, x + 2.2, top + 10, 5.2, 8, '#8a9096', '#aab0b6', '#5a6066');
    rect(c, '#3a3e44', x + 2.6, top + 12, 4.4, 0.6);
    rect(c, '#3a3e44', x + 2.6, top + 14.5, 4.4, 0.6);
    // Tangle of junction boxes and a hanging loop of spare cable (very Taipei).
    block(c, x - 5.6, top + 22, 3.4, 5, '#3a3e44', '#55606a');
    c.strokeStyle = 'rgba(30,26,22,0.9)';
    c.lineWidth = 0.7;
    c.beginPath();
    c.ellipse(x + 4, top + 28, 3, 5, 0, 0, Math.PI * 2);
    c.stroke();
  }
  // The cables, sagging to the next pole.
  c.lineWidth = 0.6;
  for (let k = 0; k < 4; k++) {
    const y0 = top + 2 + k * 2.6;
    c.strokeStyle = k % 2 ? 'rgba(30,26,22,0.85)' : 'rgba(50,46,40,0.75)';
    c.beginPath();
    c.moveTo(x, y0);
    c.quadraticCurveTo(x + w / 2, y0 + 6 + k * 1.5, x + w, y0 + (k % 2 ? 1 : -1));
    c.stroke();
  }
}

const VSIGNS: [string, string, string][] = [
  ['牛肉麵', '#c0392b', '#fff3b0'], ['藥局', '#2a8a5a', '#ffffff'], ['珍珠奶茶', '#f4f1ea', '#c0392b'], ['髮廊', '#1b1d24', '#ff6aa0'],
  ['小籠包', '#ffd34e', '#8a2a1e'], ['眼鏡', '#2a5a8a', '#ffffff'], ['滷味', '#8a2a1e', '#ffd34e'], ['旅館', '#1f3a6e', '#ffffff'],
  ['雞排', '#ff9f43', '#1b1d24'], ['按摩', '#5a2a8a', '#ffd34e'], ['手搖飲', '#2f8a7a', '#ffffff'], ['便當', '#e2493b', '#ffffff'],
];

/** A vertical Chinese shop sign jutting from an upper floor (`variant` picks the shop). Anchor = bracket foot. */
function vsign(c: Ctx, d: Decor): void {
  const v = (d.variant ?? 0) % VSIGNS.length;
  const [text, bg, fg] = VSIGNS[v];
  const x = d.x, top = d.y - 82, chars = [...text];
  const h = 8 + chars.length * 9;
  const night = isNight();
  cached(c, `sl:vs:${v}:${night ? 1 : 0}`, x - 9, top - 4, 18, h + 10, (cc) => {
    rect(cc, '#3a3e44', x - 7, top + 2, 14, 1);
    rect(cc, '#3a3e44', x - 7, top + h - 3, 14, 1);
    block(cc, x - 5.5, top, 11, h, bg, shade(bg, 0.2), shade(bg, -0.3));
    rect(cc, fg, x - 4.4, top + 1.6, 8.8, 0.5);
    rect(cc, fg, x - 4.4, top + h - 2.2, 8.8, 0.5);
    chars.forEach((ch, i) => txt(cc, ch, x, top + 8 + i * 9, 7, fg, 900, 'rgba(0,0,0,0.3)'));
    if (night) {
      const glow = fg === '#ffffff' || fg === '#fff3b0' ? bg : fg;
      ellipse(cc, `rgba(${parseInt(glow.slice(1, 3), 16)},${parseInt(glow.slice(3, 5), 16)},${parseInt(glow.slice(5, 7), 16)},0.28)`, x, top + h / 2, 9, h / 2 + 3);
      chars.forEach((ch, i) => txt(cc, ch, x, top + 8 + i * 9, 7, '#ffffff', 900));
    }
  });
}

const TRUCKS: [string, string, string, string][] = [
  ['咖啡車', 'COFFEE', '#4a3a2a', '#f8e0b0'], ['鹹酥雞', 'POPCORN CHICKEN', '#e2493b', '#ffd34e'], ['冰淇淋', 'ICE CREAM', '#ff9fc0', '#ffffff'], ['刈包', 'GUA BAO', '#2f8a7a', '#ffffff'],
];

/** A food truck with its serving hatch open, a menu board and string lights at night (`variant` picks the food). */
function foodTruck(c: Ctx, d: Decor, t: number): void {
  const v = (d.variant ?? 0) % TRUCKS.length;
  const [zh, en, col, ink] = TRUCKS[v];
  const x = d.x, y = d.y, w = 52;
  const night = isNight();
  cached(c, `sl:ft:${v}:${night ? 1 : 0}`, x - w / 2 - 8, y - 46, w + 16, 50, (cc) => {
    shadow(cc, x, y, w / 2 + 2, 3.6, 0.32);
    for (const wx of [x - 17, x + 17]) {
      ellipse(cc, '#1b1d24', wx, y - 2.5, 3.6, 3.4);
      ellipse(cc, '#9aa0a6', wx, y - 2.5, 1.4, 1.4);
    }
    block(cc, x - w / 2, y - 28, w, 24, col, shade(col, 0.2), shade(col, -0.3));
    // Cab on the right, serving hatch on the left with a propped-up awning.
    block(cc, x + w / 2 - 14, y - 26, 13, 12, shade(col, -0.1), shade(col, 0.1));
    rect(cc, night ? '#ffe8b0' : '#9fd3f0', x + w / 2 - 12, y - 24, 9, 7);
    block(cc, x - w / 2 + 4, y - 24, 26, 12, night ? '#ffe6a8' : '#f8f4ec', '#ffffff', '#b8b0a4');
    block(cc, x - w / 2 + 2, y - 34, 30, 3, shade(col, 0.35), shade(col, 0.5));
    for (let k = 0; k < 5; k++) rect(cc, k % 2 ? '#ffffff' : shade(col, 0.35), x - w / 2 + 2 + k * 6, y - 34, 6, 3);
    rect(cc, '#55606a', x - w / 2 + 3, y - 32, 0.8, 8);
    rect(cc, '#55606a', x - w / 2 + 30, y - 32, 0.8, 8);
    // Counter with goods and the server inside.
    rect(cc, '#8a6a4a', x - w / 2 + 4, y - 13, 26, 1.6);
    ellipse(cc, '#f0c8a4', x - w / 2 + 17, y - 19, 2.4, 2.4);
    rect(cc, '#2a1d16', x - w / 2 + 14.6, y - 22, 4.8, 1.6);
    block(cc, x - w / 2 + 13, y - 17, 8, 4.5, '#f4f1ea', '#ffffff');
    txt(cc, zh, x + 2, y - 31, 5.2, ink, 900, 'rgba(0,0,0,0.3)');
    txt(cc, en, x, y - 7, 2.4, ink, 800);
    // Menu board on an easel.
    block(cc, x - w / 2 - 8, y - 16, 10, 13, '#2a2420', '#4a3a30');
    for (let k = 0; k < 4; k++) rect(cc, k ? '#f4f1ea' : '#ffd34e', x - w / 2 - 6.5, y - 13 + k * 2.6, k ? 5 + (k % 2) * 2 : 6, 0.8);
    if (night) for (let k = 0; k < 6; k++) ellipse(cc, ['#ffd34e', '#ff6a6a', '#7ad0e0'][k % 3], x - w / 2 + 3 + k * 5.6, y - 36 + Math.sin(k) * 1, 1.1, 1.1);
  });
  // Steam from the hatch.
  for (let i = 0; i < 2; i++) {
    const k = (t * 0.5 + i * 0.5) % 1;
    ellipse(c, `rgba(255,255,255,${0.3 * (1 - k)})`, x - 12 + Math.sin(t * 1.6 + i * 2) * 2 + k * 3, y - 24 - k * 10, 1.5 + k * 2.5, 1.2 + k * 2);
  }
}

/** Street tree (camphor / banyan) with a cast-iron grate round the trunk. `variant`: 0 camphor, 1 banyan. */
function streetTree(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, v = (d.variant ?? 0) % 2;
  const s = hash(Math.round(x), Math.round(y), 351);
  const sway = wind.at(x, y) * 1.5 + Math.sin(t * 0.8 + s * 6) * 0.4;
  shadow(c, x, y, 16, 4, 0.3, 30);
  // Grate.
  block(c, x - 8, y - 3.4, 16, 3.4, '#4a4e54', '#6a6e74', '#2a2e33');
  for (let k = -6; k <= 6; k += 2.4) rect(c, '#2a2e33', x + k, y - 2.8, 0.6, 2.2);
  // Trunk and the crown.
  const trunk = v ? '#5a4632' : '#6a5038';
  poly(c, trunk, [x - 3, y - 2, x - 2.2, y - 24, x + 2.2, y - 24, x + 3.4, y - 2]);
  rect(c, 'rgba(255,255,255,0.12)', x - 1.6, y - 22, 0.9, 18);
  if (v) for (const [dx, h] of [[-5, 14], [5.5, 10], [-8, 8]] as const) line(c, '#5a4632', 0.7, [x + dx * 0.4, y - 30, x + dx + sway, y - 30 + h]);
  const dark = v ? '#2a5a34' : '#2f6a3a', mid = v ? '#3f8a48' : '#4a9a52', light = v ? '#6ab860' : '#7ac468';
  const cx = x + sway, cy = y - 36;
  for (const [dx, dy, r] of [[-11, 2, 10], [11, 1, 10], [0, -8, 11], [-6, -3, 9], [7, -4, 9], [0, 3, 10]] as const) ellipse(c, dark, cx + dx, cy + dy, r, r * 0.85);
  for (const [dx, dy, r] of [[-9, 0, 7], [9, -1, 7], [0, -9, 8], [-4, -5, 6], [5, -5, 6]] as const) ellipse(c, mid, cx + dx - 1, cy + dy - 1.5, r, r * 0.85);
  for (const [dx, dy, r] of [[-8, -3, 3.5], [1, -11, 4], [7, -5, 3], [-2, -5, 3]] as const) ellipse(c, light, cx + dx - 1.5, cy + dy - 1.5, r, r * 0.8);
  for (let i = 0; i < 6; i++) ellipse(c, 'rgba(0,0,0,0.12)', cx + (hash(i, 1, 352) - 0.5) * 22, cy + (hash(i, 2, 352) - 0.6) * 16, 1.4, 1);
}

/** A corrugated tin lean-to against a wall (`w` wide): rust, a water tank or a TV antenna on top (`variant`). */
function tinShed(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, w = d.w ?? 48, v = d.variant ?? 0;
  const L = x - w / 2;
  cached(c, `sl:tin:${v}:${w}`, L - 6, y - 56, w + 12, 60, (cc) => {
    shadow(cc, x, y, w / 2, 3, 0.3, 14);
    // Dark interior with stacked crates and a bicycle wheel.
    block(cc, L, y - 22, w, 22, '#3a3630', '#4a463f', '#26231f');
    for (let k = 0; k < Math.floor(w / 16); k++) {
      block(cc, L + 3 + k * 16, y - 10, 10, 8, ['#8a6a4a', '#5a7a9a', '#c8a060'][k % 3], undefined, '#2a2420');
      rect(cc, 'rgba(0,0,0,0.25)', L + 4 + k * 16, y - 6, 8, 0.6);
    }
    // Posts.
    for (const px of [L + 1, L + w - 3]) block(cc, px, y - 32, 2, 32, '#6a6660', '#8a8680', '#4a4640');
    // Sloped corrugated roof seen from the front: a band with vertical ribs, rust blotches.
    block(cc, L - 4, y - 36, w + 8, 12, '#9aa0a6', '#b8bec4', '#6a7076');
    for (let i = L - 3; i < L + w + 3; i += 2.2) rect(cc, 'rgba(0,0,0,0.14)', i, y - 36, 0.8, 12);
    for (let i = 0; i < 6; i++) {
      const rx = L + hash(i, v, 361) * w, ry = y - 34 + hash(i, v, 362) * 8;
      ellipse(cc, i % 2 ? 'rgba(160,80,30,0.55)' : 'rgba(120,60,20,0.5)', rx, ry, 2 + hash(i, v, 363) * 3, 1.2 + hash(i, v, 364) * 1.5);
    }
    rect(cc, 'rgba(255,255,255,0.3)', L - 4, y - 36, w + 8, 1);
    rect(cc, '#4a4e54', L - 4, y - 24.6, w + 8, 1.2);
    // Flat top strip (the roof seen from above) with a brick or two holding the sheets down.
    block(cc, L - 2, y - 40, w + 4, 4.5, '#7a8086', '#9aa0a6', '#5a6066');
    for (let k = 0; k < Math.floor(w / 20); k++) block(cc, L + 6 + k * 20, y - 41.4, 4, 2.4, '#a85a3a', '#c87050');
    if (v % 3 === 1) {
      // Stainless water tank on a little steel frame.
      const tx = L + w - 14;
      rect(cc, '#55606a', tx - 5, y - 44, 1, 5);
      rect(cc, '#55606a', tx + 5, y - 44, 1, 5);
      block(cc, tx - 6, y - 56, 12, 12, '#c8cdd2', '#eef2f4', '#8a9096');
      ellipse(cc, '#e8ecef', tx, y - 56, 6, 2);
      rect(cc, '#8a9096', tx - 6, y - 50, 12, 0.8);
    } else if (v % 3 === 2) {
      // TV antenna.
      const ax = L + 10;
      rect(cc, '#55606a', ax, y - 56, 0.8, 16);
      for (let k = 0; k < 4; k++) rect(cc, '#55606a', ax - 4 + k, y - 55 + k * 3, 8 - k * 2, 0.6);
    }
  });
}

/** Laundry hung from a window or a bamboo pole: `w` wide, swinging in the wind. */
function washing(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, w = d.w ?? 30, L = x - w / 2, top = y - 40;
  rect(c, '#8a7a5a', L - 2, top, w + 4, 1);
  const cols = ['#ffffff', '#3a8fd9', '#ff9fc0', '#ffd34e', '#f4f1ea', '#57c278', '#2a2f3d'];
  const n = Math.floor(w / 7);
  for (let i = 0; i < n; i++) {
    const cx = L + 3.5 + i * 7;
    const sw = Math.sin(t * 2 + i * 1.3 + x * 0.01) * 1 + wind.at(cx, top) * 2.5;
    const col = cols[Math.floor(hash(i, Math.round(x), 371) * cols.length)];
    const kind = Math.floor(hash(i, Math.round(y), 372) * 3);
    rect(c, '#e2493b', cx - 0.6, top - 1, 1.2, 2);
    if (kind === 0) poly(c, col, [cx - 2.8, top + 1, cx + 2.8, top + 1, cx + 3.4 + sw, top + 9, cx - 2.2 + sw, top + 9]);
    else if (kind === 1) poly(c, col, [cx - 2, top + 1, cx + 2, top + 1, cx + 2.6 + sw, top + 12, cx - 1.4 + sw, top + 12]);
    else poly(c, col, [cx - 3.2, top + 1, cx + 3.2, top + 1, cx + 2 + sw, top + 6, cx - 1.2 + sw, top + 6]);
    rect(c, 'rgba(0,0,0,0.1)', cx - 1 + sw * 0.5, top + 2, 0.8, kind === 1 ? 9 : 5);
  }
}

const PALETTES: string[][] = [
  ['#e2493b', '#ffd34e', '#ff8ac0'], ['#ffd34e', '#ff9f43', '#ffffff'], ['#c58cff', '#ff6aa0', '#ffffff'], ['#e2493b', '#ffffff', '#3a8fd9'], ['#ff6a3a', '#ffd34e', '#9b5fc0'],
];

/** A formal flower bed (`w` × `h`): a stone kerb, concentric rings of colour and a centre motif. `variant` picks the palette. */
function parterre(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, w = d.w ?? 64, h = d.h ?? 28, v = d.variant ?? 0;
  const pal = PALETTES[v % PALETTES.length];
  const L = x - w / 2, top = y - h;
  cached(c, `sl:pt:${v}:${w}x${h}`, L - 3, top - 8, w + 6, h + 12, (cc) => {
    block(cc, L - 2, top - 2, w + 4, h + 2, '#b8b2a6', '#d6d2c8', '#8a8478');
    rect(cc, '#5a4632', L, top, w, h - 1);
    // Rings of flower clusters, outside in.
    const rings = Math.max(1, Math.floor(Math.min(w, h) / 12));
    for (let r = 0; r < rings; r++) {
      const col = pal[r % pal.length];
      const inset = 3 + r * 6;
      for (let fx = L + inset; fx <= L + w - inset; fx += 4.4) {
        for (const fy of [top + inset, top + h - inset - 1]) {
          ellipse(cc, col, fx + hash(Math.round(fx), Math.round(fy), 381) * 1.5, fy + hash(Math.round(fx), Math.round(fy), 382) * 1.5, 2.1, 1.7);
          ellipse(cc, shade(col, 0.35), fx + 0.3, fy - 0.5, 0.9, 0.7);
        }
      }
      for (let fy = top + inset + 4; fy < top + h - inset - 2; fy += 4.4) {
        for (const fx of [L + inset, L + w - inset]) {
          ellipse(cc, col, fx + hash(Math.round(fx), Math.round(fy), 383) * 1.5, fy, 2.1, 1.7);
          ellipse(cc, shade(col, 0.35), fx + 0.3, fy - 0.5, 0.9, 0.7);
        }
      }
    }
    // Centre: a green mound with a brighter bloom.
    ellipse(cc, '#4a8a3a', x, y - h / 2, Math.max(4, w / 2 - rings * 6 - 2), Math.max(2.4, h / 2 - rings * 6 - 1));
    ellipse(cc, pal[(rings + 1) % pal.length], x, y - h / 2 - 1, 3.2, 2.6);
    ellipse(cc, '#ffffff', x - 0.6, y - h / 2 - 1.8, 1, 0.8);
    // Leaves and shade along the kerb.
    for (let i = 0; i < w / 5; i++) ellipse(cc, '#3f7a35', L + 2 + hash(i, v, 384) * (w - 4), top + 1 + hash(i, v, 385) * (h - 3), 1.6, 1.1);
    rect(cc, 'rgba(0,0,0,0.18)', L - 2, y, w + 4, 1.2);
  });
}

// ---------------------------------------------------------------------------
// Ground paint (small, so the depth sort never shows)
// ---------------------------------------------------------------------------

function manhole(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y - 3;
  ellipse(c, '#4a4e54', x, y, 6, 3.6);
  ellipse(c, '#6a6e74', x, y - 0.4, 5.2, 3);
  for (let k = -3; k <= 3; k += 1.5) rect(c, 'rgba(0,0,0,0.28)', x - 3.6 + Math.abs(k) * 0.4, y - 0.4 + k * 0.8, 7.2 - Math.abs(k) * 0.8, 0.5);
  ellipse(c, 'rgba(0,0,0,0.3)', x, y - 0.4, 1, 0.6);
}

function laneArrow(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, v = d.variant ?? 0;
  c.fillStyle = 'rgba(255,255,255,0.78)';
  if (v === 1) {
    // Turn arrow.
    rect(c, 'rgba(255,255,255,0.78)', x - 1.2, y - 12, 2.4, 10);
    poly(c, 'rgba(255,255,255,0.78)', [x - 1.2, y - 10, x + 6, y - 10, x + 6, y - 13, x + 10, y - 9, x + 6, y - 5, x + 6, y - 8, x - 1.2, y - 8]);
  } else {
    rect(c, 'rgba(255,255,255,0.78)', x - 1.2, y - 10, 2.4, 10);
    poly(c, 'rgba(255,255,255,0.78)', [x - 4, y - 9, x + 4, y - 9, x, y - 15]);
  }
}

function puddle(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y - 2, w = d.w ?? 18;
  ellipse(c, 'rgba(70,90,110,0.35)', x, y, w / 2, w / 6);
  ellipse(c, 'rgba(170,200,230,0.4)', x - w / 8, y - 0.6, w / 3.2, w / 10);
  ellipse(c, 'rgba(255,255,255,0.35)', x - w / 5, y - 1, w / 10, w / 30);
}

function leaves(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, w = d.w ?? 30, v = d.variant ?? 0;
  const cols = ['#b8862a', '#a0642a', '#c8a040', '#7a5a2a', '#d0b060'];
  for (let i = 0; i < 9; i++) {
    const lx = x + (hash(i, v, 391) - 0.5) * w, ly = y + (hash(i, v, 392) - 0.5) * (w / 3);
    c.save();
    c.translate(lx, ly);
    c.rotate(hash(i, v, 393) * Math.PI);
    ellipse(c, cols[i % cols.length], 0, 0, 1.9, 1);
    c.restore();
  }
}

/** A UBike docking station (`w` wide): white posts, a kiosk and a row of orange-and-yellow bikes. */
function ubikeDock(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, w = d.w ?? 64, L = x - w / 2;
  cached(c, `sl:ub:${w}`, L - 10, y - 34, w + 20, 38, (cc) => {
    shadow(cc, x, y, w / 2 + 2, 2.6);
    block(cc, L, y - 3, w, 3, '#8a9096', '#aab0b6', '#5a6066');
    const n = Math.floor(w / 10);
    for (let i = 0; i < n; i++) {
      const bx = L + 5 + i * 10;
      const parked = hash(i, w, 401) < 0.75;
      block(cc, bx - 1, y - 10, 2, 8, '#f4f1ea', '#ffffff', '#b8b4ac');
      if (!parked) continue;
      ellipse(cc, '#2a2e33', bx - 3, y - 4, 2.6, 2.6);
      ellipse(cc, '#2a2e33', bx + 3, y - 4, 2.6, 2.6);
      ellipse(cc, '#6a6e74', bx - 3, y - 4, 1, 1);
      ellipse(cc, '#6a6e74', bx + 3, y - 4, 1, 1);
      line(cc, '#ffb020', 1.1, [bx - 3, y - 4, bx - 0.5, y - 8.5, bx + 2.5, y - 8.5, bx + 3, y - 4]);
      line(cc, '#ffb020', 1.1, [bx - 0.5, y - 8.5, bx + 0.6, y - 4]);
      rect(cc, '#2a2e33', bx - 1.6, y - 10, 3, 1);
      block(cc, bx + 1.2, y - 12.4, 3.2, 2.2, '#f4f1ea', '#ffffff');
    }
    // Kiosk post with the YouBike-style logo.
    block(cc, L - 8, y - 30, 7, 30, '#f4f1ea', '#ffffff', '#b8b4ac');
    block(cc, L - 7.4, y - 28, 5.8, 8, '#ffb020', '#ffd060');
    txt(cc, 'U', L - 4.5, y - 24, 5, '#ffffff', 900);
    rect(cc, '#2a8a5a', L - 7.4, y - 18, 5.8, 4);
    txt(cc, 'bike', L - 4.5, y - 16, 2.6, '#ffffff', 900);
  });
}

/** A zebra-crossing crowd: people walking across (sliding), `w` = crossing width, `variant` = seed + 16 * vertical. */
function walkers(c: Ctx, d: Decor, t: number): void {
  const v = d.variant ?? 0, seed = v % 16, vert = v >= 16;
  const w = d.w ?? 64, n = 3;
  for (let i = 0; i < n; i++) {
    const speed = 9 + hash(i, seed, 411) * 5;
    const u = ((t * speed) / (w + 24) + hash(i, seed, 412)) % 1;
    const dir = hash(i, seed, 413) < 0.5 ? 1 : -1;
    const k = dir > 0 ? u : 1 - u;
    const look = Math.floor(hash(i, seed, 414) * 32);
    const r = hash(i, seed, 415);
    const acc: Acc = r < 0.2 ? 1 : r < 0.3 ? 2 : r < 0.38 ? 5 : 0;
    const lk = LOOKS[look];
    if (vert) {
      const x = d.x + (i - 1) * 9, y = d.y - w + k * w;
      drawCharacter(c, lk, x, y, { facing: dir > 0 ? 'down' : 'up', phase: (t * 2.2 + i) % 1, moving: true, blink: false, breath: 0 });
      void acc;
    } else {
      const x = d.x - w / 2 - 12 + k * (w + 24), y = d.y + (i - 1) * 6;
      drawCharacter(c, lk, x, y, { facing: dir > 0 ? 'right' : 'left', phase: (t * 2.2 + i) % 1, moving: true, blink: false, breath: 0 });
    }
  }
}

/** A stroller pushed by a parent (static). */
function stroller(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, dir = (d.w ?? 1) < 0 ? -1 : 1;
  person(c, x, y, 13 + (d.variant ?? 0), dir > 0 ? 'right' : 'left', 0, t);
  const sx = x + dir * 10;
  ellipse(c, 'rgba(20,10,6,0.22)', sx, y, 5, 1.4);
  ellipse(c, '#2a2e33', sx - 3, y - 1.4, 1.6, 1.6);
  ellipse(c, '#2a2e33', sx + 3, y - 1.4, 1.6, 1.6);
  line(c, '#55606a', 0.9, [x + dir * 4, y - 12, sx - dir * 2, y - 4]);
  block(c, sx - 4, y - 10, 8, 6, '#3a5a8a', '#4a74b0', '#2a4060');
  c.fillStyle = '#ff9fc0';
  c.beginPath();
  c.arc(sx + dir * 1, y - 10, 4.2, Math.PI, 0);
  c.fill();
  ellipse(c, '#f8d8bc', sx - dir * 1, y - 10.4, 1.6, 1.6);
}

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------

/** Kinds handled here (the package prefix stripped). Add them to the package's `animatedDecor` where they move. */
export const LIFE_ANIMATED = ['crowd', 'person', 'queue', 'benchSit', 'tripod', 'cat', 'pigeons2', 'foodTruck', 'streetTree', 'washing', 'walkers', 'stroller'];

export function drawLife(c: Ctx, kind: string, d: Decor, t: number): boolean {
  switch (kind) {
    case 'person':
      personDecor(c, d, t);
      return true;
    case 'crowd':
      crowd(c, d, t);
      return true;
    case 'queue':
      queue(c, d, t);
      return true;
    case 'benchSit':
      benchSit(c, d, t);
      return true;
    case 'tripod':
      tripod(c, d, t);
      return true;
    case 'cat':
      cat(c, d, t);
      return true;
    case 'pigeons2':
      pigeons(c, d, t);
      return true;
    case 'cables':
      cables(c, d);
      return true;
    case 'vsign':
      vsign(c, d);
      return true;
    case 'foodTruck':
      foodTruck(c, d, t);
      return true;
    case 'streetTree':
      streetTree(c, d, t);
      return true;
    case 'tinShed':
      tinShed(c, d);
      return true;
    case 'washing':
      washing(c, d, t);
      return true;
    case 'parterre':
      parterre(c, d);
      return true;
    case 'manhole':
      manhole(c, d);
      return true;
    case 'laneArrow':
      laneArrow(c, d);
      return true;
    case 'puddle':
      puddle(c, d);
      return true;
    case 'leaves':
      leaves(c, d);
      return true;
    case 'ubikeDock':
      ubikeDock(c, d);
      return true;
    case 'walkers':
      walkers(c, d, t);
      return true;
    case 'stroller':
      stroller(c, d, t);
      return true;
    default:
      return false;
  }
}
